import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PDFDocument } from "pdf-lib";
import SignaturePad from "signature_pad";
import { listarUsuarios } from "../api";
import "./ContratoTab.css";

const API_BASE = "https://te-ayudo.es/back/api";

function isUserActive(user) {
  if (
    user?.is_active === undefined &&
    user?.activo === undefined &&
    user?.active === undefined
  ) {
    return true;
  }

  const value = user?.is_active ?? user?.activo ?? user?.active;

  return value === true || value === 1 || value === "1";
}

function isSelectableProfessional(user) {
  return isUserActive(user) && Boolean(user?.id && user?.nombre);
}

export default function ConsentimientoMenoresTab({ paciente }) {
  const canvasRef = useRef(null);

  const [signaturePad, setSignaturePad] = useState(null);
  const [isSigned, setIsSigned] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);

  const [consentSpecialty, setConsentSpecialty] = useState("general");

  const [selectedProfessionalId, setSelectedProfessionalId] = useState("");
  const [professionals, setProfessionals] = useState([]);
  const [loadingProfessionals, setLoadingProfessionals] = useState(true);

  const loadProfessionals = useCallback(async () => {
    try {
      setLoadingProfessionals(true);
      setError("");

      const usersResponse = await listarUsuarios({
        page: 1,
        page_size: 200,
      });

      const usersItems = Array.isArray(usersResponse)
        ? usersResponse
        : usersResponse?.items || [];

      const sortedProfessionals = usersItems
        .filter(isSelectableProfessional)
        .sort((a, b) =>
          String(a.nombre || "").localeCompare(String(b.nombre || ""), "es", {
            sensitivity: "base",
          }),
        );

      setProfessionals(sortedProfessionals);
    } catch (err) {
      console.error(
        "[ConsentimientoMenoresTab] Error cargando profesionales:",
        err,
      );
      setError(err.message || "No se pudieron cargar los profesionales.");
      setProfessionals([]);
    } finally {
      setLoadingProfessionals(false);
    }
  }, []);

  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  const selectedProfessional = useMemo(
    () =>
      professionals.find(
        (professional) =>
          String(professional.id) === String(selectedProfessionalId),
      ) || null,
    [professionals, selectedProfessionalId],
  );

  // Limpiar el documento cuando cambie el profesional.
  useEffect(() => {
    setFileUrl(null);
    setUploadedFile(null);
    setConsentSpecialty("general");
    setMessage("");
    setError("");
  }, [selectedProfessionalId]);

  // Obtener el consentimiento PDF.
  useEffect(() => {
    if (!paciente?.id || !selectedProfessionalId) {
      return undefined;
    }

    let cancelled = false;

    const generateConsent = async () => {
      try {
        setLoading(true);
        setError("");
        setMessage("");
        setUploadedFile(null);
        setFileUrl(null);

        const params = new URLSearchParams({
          id: String(paciente.id),
          professional_id: String(selectedProfessionalId),
        });

        const response = await fetch(
          `${API_BASE}/generate_consent.php?${params.toString()}`,
          {
            credentials: "include",
          },
        );

        const text = await response.text();
        let data;

        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          throw new Error("La respuesta del servidor no es un JSON válido.");
        }

        if (!response.ok || !data.ok) {
          throw new Error(
            data.error || "Error al generar el consentimiento.",
          );
        }

        const consentUrl = data.pdf_local || data.file_url;

        if (!consentUrl) {
          throw new Error(
            "El servidor no ha devuelto el PDF del consentimiento.",
          );
        }

        if (!cancelled) {
          setConsentSpecialty(data.specialty_code || data.specialty || "general");
          setFileUrl(consentUrl);
        }
      } catch (err) {
        console.error(
          "[ConsentimientoMenoresTab] Error generando consentimiento:",
          err,
        );

        if (!cancelled) {
          setError(err.message || "No se pudo generar el consentimiento.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    generateConsent();

    return () => {
      cancelled = true;
    };
  }, [paciente?.id, selectedProfessionalId]);

  // Inicializar la firma.
  useEffect(() => {
    if (!fileUrl || !canvasRef.current) {
      return undefined;
    }

    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = 350;
    const height = 150;

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const context = canvas.getContext("2d");
    context?.scale(ratio, ratio);

    const pad = new SignaturePad(canvas, {
      backgroundColor: "rgba(0, 0, 0, 0)",
      penColor: "#000000",
    });

    const handleEndStroke = () => {
      setIsSigned(!pad.isEmpty());
    };

    if (typeof pad.addEventListener === "function") {
      pad.addEventListener("endStroke", handleEndStroke);
    } else {
      pad.onEnd = handleEndStroke;
    }

    setSignaturePad(pad);
    setIsSigned(false);

    return () => {
      if (typeof pad.removeEventListener === "function") {
        pad.removeEventListener("endStroke", handleEndStroke);
      }

      pad.off();
      pad.clear();
      setSignaturePad(null);
      setIsSigned(false);
    };
  }, [fileUrl]);

  const handleClear = () => {
    signaturePad?.clear();
    setIsSigned(false);
  };

  const handleSave = async () => {
    if (!selectedProfessionalId) {
      alert("Selecciona al profesional responsable.");
      return;
    }

    if (!signaturePad || signaturePad.isEmpty()) {
      alert("Por favor, firma antes de guardar.");
      return;
    }

    if (!fileUrl) {
      alert("No se ha podido cargar el consentimiento.");
      return;
    }

    const specialty = consentSpecialty || "general";

    try {
      setUploading(true);
      setMessage("");
      setError("");

      // 1. Cargar y firmar el PDF.
      const pdfResponse = await fetch(fileUrl);

      if (!pdfResponse.ok) {
        throw new Error(
          "No se ha podido descargar el consentimiento para firmarlo.",
        );
      }

      const pdfBytes = await pdfResponse.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();

      if (!pages.length) {
        throw new Error("El PDF no contiene páginas.");
      }

      const signatureResponse = await fetch(
        signaturePad.toDataURL("image/png"),
      );
      const pngBytes = await signatureResponse.arrayBuffer();
      const pngImage = await pdfDoc.embedPng(pngBytes);

      const page = pages[pages.length - 1];
      const { width } = page.getSize();

      const marginLeft = width - 260;
      const marginBottom = 70;
      const boxWidth = 220;
      const boxHeight = 90;

      const pngDimensions = pngImage.scale(1);
      const scale = Math.min(
        boxWidth / pngDimensions.width,
        boxHeight / pngDimensions.height,
      );

      page.drawImage(pngImage, {
        x: marginLeft,
        y: marginBottom,
        width: pngDimensions.width * scale,
        height: pngDimensions.height * scale,
      });

      const finalPdf = await pdfDoc.save();
      const blob = new Blob([finalPdf], {
        type: "application/pdf",
      });

      const signedFilename = `consentimiento_firmado_${paciente.id}_${selectedProfessionalId}_${specialty}.pdf`;

      // 2. Subir a Drive.
      const uploadFormData = new FormData();

      uploadFormData.append("file", blob, signedFilename);
      uploadFormData.append("patient_id", String(paciente.id));
      uploadFormData.append("type", "consentimiento");
      uploadFormData.append("specialty", specialty);
      uploadFormData.append(
        "professional_id",
        String(selectedProfessionalId),
      );

      const uploadResponse = await fetch(`${API_BASE}/upload_drive.php`, {
        method: "POST",
        body: uploadFormData,
        credentials: "include",
      });

      const uploadText = await uploadResponse.text();
      let uploadData;

      try {
        uploadData = uploadText ? JSON.parse(uploadText) : {};
      } catch {
        throw new Error("La respuesta de subida no es un JSON válido.");
      }

      if (!uploadResponse.ok || !uploadData.ok) {
        throw new Error(
          uploadData.error ||
            "No se pudo subir el consentimiento firmado.",
        );
      }

      setUploadedFile({
        webViewLink: uploadData.file?.webViewLink || null,
        pdf_local: uploadData.pdf_local || null,
      });

      // 3. Enviar el PDF firmado por correo.
      const mailFormData = new FormData();

      mailFormData.append("file", blob, signedFilename);
      mailFormData.append("patient_id", String(paciente.id));
      mailFormData.append("type", "consentimiento");
      mailFormData.append("specialty", specialty);
      mailFormData.append(
        "professional_id",
        String(selectedProfessionalId),
      );

      const mailResponse = await fetch(
        `${API_BASE}/send_consent_mail.php`,
        {
          method: "POST",
          body: mailFormData,
          credentials: "include",
        },
      );

      const mailText = await mailResponse.text();
      let mailData;

      try {
        mailData = mailText ? JSON.parse(mailText) : {};
      } catch {
        throw new Error(
          "El consentimiento se ha subido, pero la respuesta del correo no es válida.",
        );
      }

      if (mailResponse.ok && mailData.ok) {
        setMessage(
          "✅ Consentimiento firmado, subido y enviado por correo correctamente.",
        );
      } else {
        setMessage(
          `⚠️ Consentimiento subido, pero no se pudo enviar el correo: ${
            mailData.error || "error desconocido"
          }`,
        );
      }
    } catch (err) {
      console.error(
        "[ConsentimientoMenoresTab] Error guardando consentimiento:",
        err,
      );
      setMessage(
        `❌ ${err.message || "No se pudo guardar el consentimiento."}`,
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="contrato-tab">
      <h3>🧾 Consentimiento expreso de menores</h3>

      <div className="form-row">
        <label htmlFor="consent-professional">Profesional responsable</label>

        <select
          id="consent-professional"
          value={selectedProfessionalId}
          onChange={(event) =>
            setSelectedProfessionalId(event.target.value)
          }
          disabled={loadingProfessionals}
        >
          <option value="">
            {loadingProfessionals
              ? "Cargando profesionales..."
              : "-- Selecciona un profesional --"}
          </option>

          {professionals.map((professional) => (
            <option key={professional.id} value={professional.id}>
              {professional.nombre}
            </option>
          ))}
        </select>

        {selectedProfessional && (
          <p className="form-help">
            Profesional seleccionado:{" "}
            <strong>{selectedProfessional.nombre}</strong>
          </p>
        )}
      </div>

      {loading && <p>Generando consentimiento personalizado...</p>}

      {error && <p style={{ color: "red" }}>❌ {error}</p>}

      {!loading && fileUrl && (
        <>
          <div className="contrato-preview-wrapper">
            <iframe
              src={fileUrl}
              className="contrato-preview"
              title="Vista previa del consentimiento"
            />
          </div>

          <p className="contrato-open-fullscreen-row">
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-open-fullscreen"
            >
              📄 Abrir consentimiento a pantalla completa
            </a>
          </p>
        </>
      )}

      {fileUrl && (
        <div className="signature-section">
          <h4>✍️ Firma del tutor legal</h4>

          <div className="signature-box">
            <canvas ref={canvasRef} className="signature-canvas" />
          </div>

          <div className="signature-actions">
            <button
              type="button"
              onClick={handleClear}
              disabled={uploading || !isSigned}
            >
              Borrar firma
            </button>

            <button
              type="button"
              onClick={handleSave}
              className="primary-btn"
              disabled={
                uploading ||
                !isSigned ||
                !selectedProfessionalId
              }
            >
              {uploading
                ? "Guardando..."
                : "Guardar consentimiento firmado"}
            </button>
          </div>
        </div>
      )}

      {message && (
        <div className="message-box">
          <p>{message}</p>

          {uploadedFile && (
            <button
              type="button"
              className="btn-ver-contrato"
              onClick={() => {
                const url =
                  uploadedFile.pdf_local || uploadedFile.webViewLink;

                if (url) {
                  window.open(url, "_blank", "noopener,noreferrer");
                }
              }}
            >
              📄 Ver consentimiento firmado
            </button>
          )}
        </div>
      )}
    </div>
  );
}